import React, { useState, useEffect, useRef } from 'react';
import {
  Plus, Edit2, Trash2, Eye, EyeOff, ToggleLeft, ToggleRight,
  Image, Clock, Users, TrendingUp, Sparkles, Flame, Gift,
  ChefHat, AlertCircle, Calendar, Save, X, Smartphone,
  BarChart3, MousePointer, ShoppingCart, Percent, Upload, Loader2,
  DollarSign, Target, Award, TrendingDown, MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';

// Timezone helpers for Sonora (UTC-7)
const SONORA_OFFSET_HOURS = -7;

// Convert UTC date to Sonora time for display in datetime-local input
const utcToSonoraInput = (utcDateStr) => {
  if (!utcDateStr) return '';
  const utcDate = new Date(utcDateStr);
  // Add offset to get Sonora time
  const sonoraDate = new Date(utcDate.getTime() + (SONORA_OFFSET_HOURS * 60 * 60 * 1000));
  // Format for datetime-local input (YYYY-MM-DDTHH:mm)
  return sonoraDate.toISOString().slice(0, 16);
};

// Convert Sonora input to UTC for saving to backend
const sonoraInputToUtc = (localDateStr) => {
  if (!localDateStr) return null;
  // The input is in Sonora time, we need to convert to UTC
  const localDate = new Date(localDateStr);
  // Subtract offset to get UTC
  const utcDate = new Date(localDate.getTime() - (SONORA_OFFSET_HOURS * 60 * 60 * 1000));
  return utcDate.toISOString();
};

const TEMPLATES = {
  elegante: { id: 'elegante', name: 'Elegante', gradient: 'from-slate-700 to-slate-900', description: 'Sofisticado y premium' },
  fiesta: { id: 'fiesta', name: 'Fiesta', gradient: 'from-pink-600 to-purple-700', description: 'Colorido y festivo' },
  urgente: { id: 'urgente', name: 'Urgente', gradient: 'from-red-600 to-orange-600', description: 'Crea urgencia' },
  gourmet: { id: 'gourmet', name: 'Gourmet', gradient: 'from-emerald-600 to-teal-700', description: 'Natural y fresco' },
  tropical: { id: 'tropical', name: 'Tropical', gradient: 'from-cyan-600 to-blue-700', description: 'Veraniego y fresco' },
};

const TEMPLATES_LIST = Object.values(TEMPLATES);

const BADGES = [
  { id: '', name: 'Sin badge', icon: null },
  { id: 'mas_vendido', name: 'Más Vendido', icon: Flame, color: 'bg-orange-500' },
  { id: 'nuevo', name: 'Nuevo', icon: Sparkles, color: 'bg-blue-500' },
  { id: 'ultima_oportunidad', name: 'Última Oportunidad', icon: AlertCircle, color: 'bg-red-500' },
  { id: 'chef_recomienda', name: 'Chef Recomienda', icon: ChefHat, color: 'bg-emerald-500' },
];

const FREQUENCIES = [
  { id: 'every_visit', name: 'Cada visita', description: 'Mostrar siempre que abran la app' },
  { id: 'once_per_day', name: 'Una vez al día', description: 'No molestar, mostrar máximo 1 vez por día' },
  { id: 'first_time_only', name: 'Solo primera vez', description: 'Mostrar solo a nuevos visitantes' },
];

const PromoStudio = () => {
  const [promotions, setPromotions] = useState([]);
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewPromo, setPreviewPromo] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showVisitors, setShowVisitors] = useState(false);
  const [visitors, setVisitors] = useState([]);
  const [loadingVisitors, setLoadingVisitors] = useState(false);
  const [selectedPromoForVisitors, setSelectedPromoForVisitors] = useState(null);
  const [showSalesAnalytics, setShowSalesAnalytics] = useState(false);
  const [salesData, setSalesData] = useState(null);
  const [loadingSales, setLoadingSales] = useState(false);
  const [showPromoDetail, setShowPromoDetail] = useState(false);
  const [promoDetailData, setPromoDetailData] = useState(null);
  const [loadingPromoDetail, setLoadingPromoDetail] = useState(false);
  const fileInputRef = useRef(null);
  
  // Carousel/Featured Products state
  const [showCarouselManager, setShowCarouselManager] = useState(false);
  const [carouselItems, setCarouselItems] = useState([]);
  const [loadingCarousel, setLoadingCarousel] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  
  // Leads state
  const [showLeadsPanel, setShowLeadsPanel] = useState(false);
  const [leads, setLeads] = useState([]);
  const [leadsStats, setLeadsStats] = useState(null);
  const [loadingLeads, setLoadingLeads] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const [form, setForm] = useState({
    title: '',
    subtitle: '',
    description: '',
    image_url: '',
    original_price: '',
    promo_price: '',
    product_id: '',
    product_name: '',
    badge: '',
    template: 'elegante',
    start_date: '',
    end_date: '',
    frequency: 'once_per_day',
    show_countdown: true,
    show_social_proof: true,
    is_active: false,
    sound_enabled: false,
  });

  useEffect(() => {
    fetchPromotions();
    fetchProducts();
    fetchStats();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const fetchPromotions = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/promotions`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setPromotions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching promotions:', error);
      toast.error('Error al cargar promociones');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/products`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setProducts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/promotions/stats/summary`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Fetch carousel items (promos + featured products)
  const fetchCarouselItems = async () => {
    setLoadingCarousel(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/promotions/carousel-items`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setCarouselItems(data.items || []);
    } catch (error) {
      console.error('Error fetching carousel:', error);
      toast.error('Error al cargar carrusel');
    } finally {
      setLoadingCarousel(false);
    }
  };

  // Toggle featured status for a product
  const toggleFeatured = async (productId, currentFeatured) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/promotions/products/${productId}/featured?featured=${!currentFeatured}`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success(!currentFeatured ? 'Producto destacado' : 'Producto removido del carrusel');
        fetchCarouselItems();
        fetchProducts(); // Refresh products list
      }
    } catch (error) {
      console.error('Error toggling featured:', error);
      toast.error('Error al actualizar producto');
    }
  };

  // Fetch leads
  const fetchLeads = async () => {
    setLoadingLeads(true);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/promotions/leads`, { headers: getAuthHeaders() }),
        fetch(`${BACKEND_URL}/api/promotions/leads/stats`, { headers: getAuthHeaders() })
      ]);
      
      const leadsData = await leadsRes.json();
      const statsData = await statsRes.json();
      
      setLeads(leadsData.leads || []);
      setLeadsStats(statsData);
      setShowLeadsPanel(true);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Error al cargar leads');
    } finally {
      setLoadingLeads(false);
    }
  };

  // Update lead status
  const updateLeadStatus = async (leadId, status) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/promotions/leads/${leadId}/status?status=${status}`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success('Estado actualizado');
        fetchLeads();
      }
    } catch (error) {
      console.error('Error updating lead:', error);
      toast.error('Error al actualizar');
    }
  };

  const fetchVisitors = async (promoId = null) => {
    setLoadingVisitors(true);
    try {
      const url = promoId 
        ? `${BACKEND_URL}/api/promotions/${promoId}/visitors?limit=100`
        : `${BACKEND_URL}/api/promotions/visitors/all?limit=100`;
      
      const res = await fetch(url, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setVisitors(data.visitors || []);
      setSelectedPromoForVisitors(promoId);
      setShowVisitors(true);
    } catch (error) {
      console.error('Error fetching visitors:', error);
      toast.error('Error al cargar visitantes');
    } finally {
      setLoadingVisitors(false);
    }
  };

  const fetchSalesAnalytics = async () => {
    setLoadingSales(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/promotions/analytics/sales`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setSalesData(data);
      setShowSalesAnalytics(true);
    } catch (error) {
      console.error('Error fetching sales:', error);
      toast.error('Error al cargar análisis de ventas');
    } finally {
      setLoadingSales(false);
    }
  };

  const fetchPromoDetail = async (promoId, promoTitle) => {
    setLoadingPromoDetail(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/promotions/analytics/sales/${promoId}`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setPromoDetailData({ ...data, title: promoTitle });
      setShowPromoDetail(true);
    } catch (error) {
      console.error('Error fetching promo detail:', error);
      toast.error('Error al cargar detalle de promoción');
    } finally {
      setLoadingPromoDetail(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...form,
        original_price: parseFloat(form.original_price) || 0,
        promo_price: parseFloat(form.promo_price) || 0,
        start_date: sonoraInputToUtc(form.start_date),
        end_date: sonoraInputToUtc(form.end_date),
      };

      const url = editingPromo 
        ? `${BACKEND_URL}/api/promotions/${editingPromo.id}`
        : `${BACKEND_URL}/api/promotions`;
      
      const method = editingPromo ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error('Error saving promotion');

      toast.success(editingPromo ? 'Promoción actualizada' : 'Promoción creada');
      resetForm();
      fetchPromotions();
      fetchStats();
    } catch (error) {
      console.error('Error saving promotion:', error);
      toast.error('Error al guardar promoción');
    }
  };

  const handleToggle = async (promo) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/promotions/${promo.id}/toggle`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (!res.ok) throw new Error('Error toggling promotion');
      
      const data = await res.json();
      toast.success(data.is_active ? 'Promoción activada' : 'Promoción desactivada');
      fetchPromotions();
      fetchStats();
    } catch (error) {
      console.error('Error toggling promotion:', error);
      toast.error('Error al cambiar estado');
    }
  };

  const handleDelete = async (promoId) => {
    if (!window.confirm('¿Eliminar esta promoción?')) return;
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/promotions/${promoId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (!res.ok) throw new Error('Error deleting promotion');
      
      toast.success('Promoción eliminada');
      fetchPromotions();
      fetchStats();
    } catch (error) {
      console.error('Error deleting promotion:', error);
      toast.error('Error al eliminar');
    }
  };

  const handleEdit = (promo) => {
    setEditingPromo(promo);
    setForm({
      title: promo.title || '',
      subtitle: promo.subtitle || '',
      description: promo.description || '',
      image_url: promo.image_url || '',
      original_price: promo.original_price?.toString() || '',
      promo_price: promo.promo_price?.toString() || '',
      product_id: promo.product_id || '',
      product_name: promo.product_name || '',
      badge: promo.badge || '',
      template: promo.template || 'elegante',
      start_date: utcToSonoraInput(promo.start_date),
      end_date: utcToSonoraInput(promo.end_date),
      frequency: promo.frequency || 'once_per_day',
      show_countdown: promo.show_countdown ?? true,
      show_social_proof: promo.show_social_proof ?? true,
      is_active: promo.is_active ?? false,
      sound_enabled: promo.sound_enabled ?? false,
    });
    setShowForm(true);
  };

  const handleProductSelect = (productId) => {
    if (!productId) {
      // Clear product selection
      setForm(prev => ({
        ...prev,
        product_id: '',
        product_name: '',
      }));
      return;
    }
    
    const product = products.find(p => (p.id || p.product_id) === productId);
    if (product) {
      console.log('Selected product:', product); // Debug
      setForm(prev => ({
        ...prev,
        product_id: product.id || product.product_id,
        product_name: product.name,
        original_price: product.price?.toString() || '',
        // ALWAYS use product image if available
        image_url: product.image_url || '',
        title: prev.title || product.name,
      }));
    }
  };

  const resetForm = () => {
    setForm({
      title: '',
      subtitle: '',
      description: '',
      image_url: '',
      original_price: '',
      promo_price: '',
      product_id: '',
      product_name: '',
      badge: '',
      template: 'elegante',
      start_date: '',
      end_date: '',
      frequency: 'once_per_day',
      show_countdown: true,
      show_social_proof: true,
      is_active: false,
      sound_enabled: false,
    });
    setEditingPromo(null);
    setShowForm(false);
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten archivos de imagen');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no debe superar 5MB');
      return;
    }

    setUploadingImage(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Error al subir imagen');
      }

      const data = await res.json();
      
      // The endpoint returns the URL in 'url' field
      const imageUrl = data.url || data.image_url;
      
      setForm(prev => ({ ...prev, image_url: imageUrl }));
      toast.success('¡Imagen subida correctamente!');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error(error.message || 'Error al subir la imagen');
    } finally {
      setUploadingImage(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const formatPrice = (price) => `$${parseFloat(price || 0).toFixed(0)}`;

  const calculateSavings = () => {
    const original = parseFloat(form.original_price) || 0;
    const promo = parseFloat(form.promo_price) || 0;
    const savings = original - promo;
    const percent = original > 0 ? Math.round((savings / original) * 100) : 0;
    return { savings, percent };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
            <Gift className="w-8 h-8 text-orange-500" />
            Promo Studio
          </h1>
          <p className="text-slate-500 mt-1">Crea promociones popup espectaculares para tu menú digital</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nueva Promoción
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Gift className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats.total_promotions}</p>
                <p className="text-xs text-slate-500">Total</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <ToggleRight className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats.active_promotions}</p>
                <p className="text-xs text-slate-500">Activas</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Eye className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats.total_views}</p>
                <p className="text-xs text-slate-500">Vistas</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <MousePointer className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats.total_clicks}</p>
                <p className="text-xs text-slate-500">Clicks</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats.overall_conversion_rate}%</p>
                <p className="text-xs text-slate-500">Conversión</p>
              </div>
            </div>
          </div>
          {/* Ver Visitantes Button */}
          <div 
            onClick={() => fetchVisitors()}
            className="bg-white rounded-xl p-4 shadow-sm border cursor-pointer hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-100 rounded-lg">
                <Users className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <p className="text-sm font-bold text-cyan-600">Ver Visitantes</p>
                <p className="text-xs text-slate-500">Historial detallado</p>
              </div>
            </div>
          </div>
          {/* Ver Ventas Button */}
          <div 
            onClick={() => fetchSalesAnalytics()}
            className="bg-white rounded-xl p-4 shadow-sm border cursor-pointer hover:bg-green-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-bold text-green-600">Ver Ventas</p>
                <p className="text-xs text-slate-500">Análisis y estrategia</p>
              </div>
            </div>
          </div>
          {/* Editar Carrusel Button */}
          <div 
            onClick={() => { fetchCarouselItems(); setShowCarouselManager(true); }}
            className="bg-white rounded-xl p-4 shadow-sm border cursor-pointer hover:bg-orange-50 transition-colors col-span-2 md:col-span-1"
            data-testid="edit-carousel-btn"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Smartphone className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm font-bold text-orange-600">Editar Carrusel</p>
                <p className="text-xs text-slate-500">Ofertas del Día</p>
              </div>
            </div>
          </div>
          {/* Ver Leads Button */}
          <div 
            onClick={() => fetchLeads()}
            className="bg-white rounded-xl p-4 shadow-sm border cursor-pointer hover:bg-cyan-50 transition-colors"
            data-testid="view-leads-btn"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-100 rounded-lg">
                <Users className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <p className="text-sm font-bold text-cyan-600">Leads Interesados</p>
                <p className="text-xs text-slate-500">Clientes potenciales</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Promotions List */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b bg-slate-50">
          <h2 className="font-semibold text-slate-700">Mis Promociones</h2>
        </div>
        
        {promotions.length === 0 ? (
          <div className="p-12 text-center">
            <Gift className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-600 mb-2">No hay promociones</h3>
            <p className="text-slate-400 mb-4">Crea tu primera promoción para atraer más clientes</p>
            <button
              onClick={() => setShowForm(true)}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              Crear Promoción
            </button>
          </div>
        ) : (
          <div className="divide-y">
            {promotions.map((promo) => (
              <div key={promo.id} className="p-4 hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-4">
                  {/* Image */}
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                    {promo.image_url ? (
                      <img src={promo.image_url} alt={promo.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Image className="w-8 h-8 text-slate-300" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-slate-800 truncate">{promo.title}</h3>
                      {promo.badge && (
                        <span className={`px-2 py-0.5 text-xs font-medium text-white rounded-full ${BADGES.find(b => b.id === promo.badge)?.color || 'bg-slate-500'}`}>
                          {BADGES.find(b => b.id === promo.badge)?.name}
                        </span>
                      )}
                    </div>
                    {/* Description */}
                    {promo.description && (
                      <p className="text-xs text-amber-600 mb-1 truncate">📋 {promo.description}</p>
                    )}
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-slate-400 line-through">{formatPrice(promo.original_price)}</span>
                      <span className="font-bold text-orange-500">{formatPrice(promo.promo_price)}</span>
                      <span className="text-green-600 text-xs">-{promo.savings_percent}%</span>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" /> {promo.views_count} vistas
                      </span>
                      <span className="flex items-center gap-1">
                        <MousePointer className="w-3 h-3" /> {promo.clicks_count} clicks
                      </span>
                      <span className="flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" /> {promo.conversion_rate}% conv.
                      </span>
                      <button 
                        onClick={() => fetchVisitors(promo.id)}
                        className="flex items-center gap-1 text-cyan-500 hover:text-cyan-600 hover:underline"
                      >
                        <Users className="w-3 h-3" /> Ver visitantes
                      </button>
                    </div>
                  </div>

                  {/* Status Toggle */}
                  <button
                    onClick={() => handleToggle(promo)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      promo.is_active 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-slate-100 text-slate-500'
                    }`}
                  >
                    {promo.is_active ? (
                      <>
                        <ToggleRight className="w-5 h-5" />
                        Activa
                      </>
                    ) : (
                      <>
                        <ToggleLeft className="w-5 h-5" />
                        Inactiva
                      </>
                    )}
                  </button>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { setPreviewPromo(promo); setShowPreview(true); }}
                      className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                      title="Vista previa"
                    >
                      <Smartphone className="w-5 h-5 text-slate-400" />
                    </button>
                    <button
                      onClick={() => handleEdit(promo)}
                      className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                      title="Editar"
                    >
                      <Edit2 className="w-5 h-5 text-slate-400" />
                    </button>
                    <button
                      onClick={() => handleDelete(promo.id)}
                      className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 className="w-5 h-5 text-red-400" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-6 py-4 border-b flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-800">
                {editingPromo ? 'Editar Promoción' : 'Nueva Promoción'}
              </h2>
              <button onClick={resetForm} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Product Selector */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Vincular Producto (opcional)
                </label>
                <select
                  value={form.product_id}
                  onChange={(e) => handleProductSelect(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">Seleccionar producto...</option>
                  {products.map((product) => (
                    <option key={product.id || product.product_id} value={product.id || product.product_id}>
                      {product.name} - ${product.price}
                    </option>
                  ))}
                </select>
                {form.product_id && form.product_name && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                    <span className="text-green-600 text-sm">✓ Producto vinculado:</span>
                    <span className="font-medium text-green-700">{form.product_name}</span>
                  </div>
                )}
              </div>

              {/* Title & Subtitle */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Título *
                  </label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Ej: 2x1 en Cocteles"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Subtítulo
                  </label>
                  <input
                    type="text"
                    value={form.subtitle}
                    onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Ej: Solo por hoy"
                  />
                </div>
              </div>

              {/* Prices */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Precio Original *
                  </label>
                  <input
                    type="number"
                    value={form.original_price}
                    onChange={(e) => setForm({ ...form, original_price: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="$0"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Precio Promocional *
                  </label>
                  <input
                    type="number"
                    value={form.promo_price}
                    onChange={(e) => setForm({ ...form, promo_price: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="$0"
                    required
                  />
                </div>
                <div className="flex items-end">
                  <div className="w-full px-4 py-2 bg-green-50 border border-green-200 rounded-lg text-center">
                    <p className="text-xs text-green-600">Ahorro</p>
                    <p className="font-bold text-green-700">
                      -{calculateSavings().percent}% ({formatPrice(calculateSavings().savings)})
                    </p>
                  </div>
                </div>
              </div>

              {/* Image Section */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Imagen de la Promoción
                </label>
                
                {/* Show image from linked product */}
                {form.image_url && form.product_id && (
                  <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 text-green-700 text-sm mb-2">
                      <span>✓</span>
                      <span className="font-medium">Imagen del producto vinculado</span>
                    </div>
                    <div className="relative group">
                      <div className="w-full h-48 rounded-lg overflow-hidden bg-white border">
                        <img 
                          src={form.image_url.startsWith('/api') ? `${BACKEND_URL}${form.image_url}` : form.image_url}
                          alt="Preview" 
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, image_url: '' })}
                        className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Usar otra imagen"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Show upload option only if no image */}
                {!form.image_url && (
                  <>
                    {/* Upload Button */}
                    <div className="flex gap-3 mb-3">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="promo-image-upload"
                      />
                      <label
                        htmlFor="promo-image-upload"
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors
                          ${uploadingImage ? 'bg-orange-50 border-orange-300' : 'border-slate-300 hover:border-orange-400 hover:bg-orange-50'}`}
                      >
                        {uploadingImage ? (
                          <>
                            <Loader2 className="w-5 h-5 text-orange-500 animate-spin" />
                            <span className="text-orange-600 font-medium">Subiendo...</span>
                          </>
                        ) : (
                          <>
                            <Upload className="w-5 h-5 text-slate-500" />
                            <span className="text-slate-600">Subir imagen</span>
                          </>
                        )}
                      </label>
                    </div>

                    {/* URL Input (alternative) */}
                    <div className="relative">
                      <input
                        type="url"
                        value={form.image_url}
                        onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="O pega una URL de imagen..."
                      />
                    </div>
                  </>
                )}

                {/* Image Preview for manually added images (not from product) */}
                {form.image_url && !form.product_id && (
                  <div className="mt-3 relative group">
                    <div className="w-full h-48 rounded-lg overflow-hidden bg-slate-100 border">
                      <img 
                        src={form.image_url.startsWith('/api') ? `${BACKEND_URL}${form.image_url}` : form.image_url}
                        alt="Preview" 
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="hidden w-full h-full items-center justify-center text-slate-400">
                        <span>Error al cargar imagen</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, image_url: '' })}
                      className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Template */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Template Visual
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {TEMPLATES_LIST.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setForm({ ...form, template: t.id })}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        form.template === t.id ? 'border-orange-500 ring-2 ring-orange-200' : 'border-transparent'
                      }`}
                    >
                      <div className={`h-8 rounded bg-gradient-to-r ${t.gradient} mb-1`} />
                      <p className="text-xs font-medium text-slate-700">{t.name}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Badge */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Badge
                </label>
                <div className="flex flex-wrap gap-2">
                  {BADGES.map((b) => (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => setForm({ ...form, badge: b.id })}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-all ${
                        form.badge === b.id 
                          ? `${b.color || 'bg-slate-200'} text-white` 
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {b.icon && <b.icon className="w-4 h-4" />}
                      {b.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Fecha Inicio (opcional)
                  </label>
                  <input
                    type="datetime-local"
                    value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Fecha Fin (opcional)
                  </label>
                  <input
                    type="datetime-local"
                    value={form.end_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
              </div>

              {/* Frequency */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Frecuencia de Visualización
                </label>
                <div className="space-y-2">
                  {FREQUENCIES.map((f) => (
                    <label
                      key={f.id}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                        form.frequency === f.id ? 'border-orange-500 bg-orange-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="frequency"
                        value={f.id}
                        checked={form.frequency === f.id}
                        onChange={(e) => setForm({ ...form, frequency: e.target.value })}
                        className="text-orange-500 focus:ring-orange-500"
                      />
                      <div>
                        <p className="font-medium text-slate-700">{f.name}</p>
                        <p className="text-xs text-slate-400">{f.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Toggles */}
              <div className="grid grid-cols-2 gap-4">
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={form.show_countdown}
                    onChange={(e) => setForm({ ...form, show_countdown: e.target.checked })}
                    className="w-5 h-5 text-orange-500 rounded focus:ring-orange-500"
                  />
                  <div>
                    <p className="font-medium text-slate-700">Cuenta Regresiva</p>
                    <p className="text-xs text-slate-400">Mostrar tiempo restante</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={form.show_social_proof}
                    onChange={(e) => setForm({ ...form, show_social_proof: e.target.checked })}
                    className="w-5 h-5 text-orange-500 rounded focus:ring-orange-500"
                  />
                  <div>
                    <p className="font-medium text-slate-700">Prueba Social</p>
                    <p className="text-xs text-slate-400">"X personas lo pidieron"</p>
                  </div>
                </label>
              </div>

              {/* Active Toggle */}
              <label className="flex items-center gap-3 p-4 border-2 border-dashed rounded-lg cursor-pointer hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="w-6 h-6 text-green-500 rounded focus:ring-green-500"
                />
                <div>
                  <p className="font-bold text-slate-700">Activar Promoción</p>
                  <p className="text-sm text-slate-400">La promoción se mostrará inmediatamente en el menú digital</p>
                </div>
              </label>

              {/* Submit */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 px-4 py-3 border rounded-lg text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center justify-center gap-2"
                >
                  <Save className="w-5 h-5" />
                  {editingPromo ? 'Guardar Cambios' : 'Crear Promoción'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && previewPromo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
          <div className="relative">
            <button
              onClick={() => setShowPreview(false)}
              className="absolute -top-12 right-0 text-white hover:text-slate-300"
            >
              <X className="w-8 h-8" />
            </button>
            <div className="bg-slate-900 rounded-3xl p-2 shadow-2xl" style={{ width: '375px' }}>
              <div className="bg-black rounded-2xl overflow-hidden" style={{ height: '667px' }}>
                {/* Mini Phone Frame */}
                <div className="h-full overflow-y-auto">
                  {/* Simulated promo popup */}
                  {(() => {
                    const template = TEMPLATES[previewPromo.template] || TEMPLATES.elegante;
                    const originalPrice = parseFloat(previewPromo.original_price) || 0;
                    const promoPrice = parseFloat(previewPromo.promo_price) || 0;
                    const savings = originalPrice - promoPrice;
                    const imageUrl = previewPromo.image_url?.startsWith('/api') 
                      ? `${BACKEND_URL}${previewPromo.image_url}` 
                      : previewPromo.image_url;
                    
                    return (
                      <div className={`h-full bg-gradient-to-br ${template.gradient} p-4 flex flex-col`}>
                        <div className="flex justify-end mb-4">
                          <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                            <X className="w-4 h-4 text-white" />
                          </div>
                        </div>
                        
                        <div className="text-center mb-4">
                          <span className="px-4 py-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full text-white text-sm font-bold">
                            ✨ PROMOCIÓN ESPECIAL ✨
                          </span>
                        </div>

                        <div className="flex-1 flex flex-col items-center justify-center">
                          {imageUrl ? (
                            <div className="w-48 h-36 rounded-xl overflow-hidden mb-4 bg-white/10">
                              <img 
                                src={imageUrl} 
                                alt="" 
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            </div>
                          ) : (
                            <div className="w-48 h-36 bg-white/10 rounded-xl flex items-center justify-center mb-4">
                              <Gift className="w-16 h-16 text-white/30" />
                            </div>
                          )}

                          <h2 className="text-2xl font-bold text-white mb-2">{previewPromo.title || 'Título de Promoción'}</h2>
                          {previewPromo.subtitle && (
                            <p className="text-white/70 mb-4">{previewPromo.subtitle}</p>
                          )}

                          <div className="flex items-center gap-3 mb-4">
                            <span className="text-white/50 line-through text-lg">${originalPrice}</span>
                            <span className="text-3xl font-bold text-amber-400">${promoPrice}</span>
                          </div>

                          {savings > 0 && (
                            <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
                              Ahorras ${savings}
                            </span>
                          )}
                        </div>

                        <button className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold rounded-xl flex items-center justify-center gap-2">
                          <ShoppingCart className="w-5 h-5" />
                          ¡LO QUIERO!
                        </button>

                        <p className="text-center text-white/40 text-sm mt-4">Ver menú completo →</p>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Visitors Modal */}
      {showVisitors && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Users className="w-6 h-6 text-white" />
                <h2 className="text-xl font-bold text-white">
                  {selectedPromoForVisitors ? 'Visitantes de Promoción' : 'Todos los Visitantes'}
                </h2>
              </div>
              <button 
                onClick={() => setShowVisitors(false)}
                className="text-white/80 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {loadingVisitors ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
                </div>
              ) : visitors.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No hay visitantes registrados aún</p>
                  <p className="text-sm text-slate-400 mt-2">Los visitantes aparecerán cuando vean tus promociones</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {visitors.map((visitor, idx) => (
                    <div key={idx} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg border">
                      {/* Icon based on action */}
                      <div className={`p-2 rounded-full ${
                        visitor.action === 'click' ? 'bg-amber-100' : 'bg-purple-100'
                      }`}>
                        {visitor.action === 'click' ? (
                          <MousePointer className={`w-4 h-4 ${visitor.action === 'click' ? 'text-amber-600' : 'text-purple-600'}`} />
                        ) : (
                          <Eye className="w-4 h-4 text-purple-600" />
                        )}
                      </div>
                      
                      {/* Visitor Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          {visitor.is_registered ? (
                            <>
                              <span className="font-medium text-slate-700">
                                {visitor.visitor_name || 'Cliente'}
                              </span>
                              {visitor.visitor_phone && (
                                <span className="text-sm text-slate-500">
                                  ({visitor.visitor_phone})
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-slate-500 italic">Visitante anónimo</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-400 mt-1">
                          <span className={`px-2 py-0.5 rounded-full ${
                            visitor.action === 'click' 
                              ? 'bg-amber-100 text-amber-700' 
                              : 'bg-purple-100 text-purple-700'
                          }`}>
                            {visitor.action === 'click' ? '🛒 Agregó al carrito' : '👁️ Vio promoción'}
                          </span>
                          {visitor.promo_title && !selectedPromoForVisitors && (
                            <span className="text-slate-500">• {visitor.promo_title}</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Timestamp */}
                      <div className="text-right">
                        <p className="text-xs text-slate-400">
                          {new Date(visitor.timestamp).toLocaleDateString('es-MX', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t bg-slate-50 flex justify-between items-center">
              <p className="text-sm text-slate-500">
                {visitors.length} registros encontrados
              </p>
              <button
                onClick={() => setShowVisitors(false)}
                className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sales Analytics Modal */}
      {showSalesAnalytics && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <BarChart3 className="w-6 h-6 text-white" />
                <div>
                  <h2 className="text-xl font-bold text-white">Análisis de Ventas</h2>
                  <p className="text-white/70 text-sm">Métricas para tomar decisiones estratégicas</p>
                </div>
              </div>
              <button 
                onClick={() => setShowSalesAnalytics(false)}
                className="text-white/80 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              {loadingSales ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-green-500" />
                </div>
              ) : salesData ? (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="w-5 h-5 text-green-600" />
                        <span className="text-sm text-green-700 font-medium">Ingresos Totales</span>
                      </div>
                      <p className="text-2xl font-bold text-green-800">
                        ${salesData.summary?.total_revenue?.toLocaleString() || 0}
                      </p>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                      <div className="flex items-center gap-2 mb-2">
                        <ShoppingCart className="w-5 h-5 text-blue-600" />
                        <span className="text-sm text-blue-700 font-medium">Unidades Vendidas</span>
                      </div>
                      <p className="text-2xl font-bold text-blue-800">
                        {salesData.summary?.total_units_sold || 0}
                      </p>
                    </div>
                    <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Gift className="w-5 h-5 text-amber-600" />
                        <span className="text-sm text-amber-700 font-medium">Descuentos Dados</span>
                      </div>
                      <p className="text-2xl font-bold text-amber-800">
                        ${salesData.summary?.total_savings_given?.toLocaleString() || 0}
                      </p>
                    </div>
                    <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Target className="w-5 h-5 text-purple-600" />
                        <span className="text-sm text-purple-700 font-medium">Promos Activas</span>
                      </div>
                      <p className="text-2xl font-bold text-purple-800">
                        {salesData.summary?.active_promotions || 0} / {salesData.summary?.total_promotions || 0}
                      </p>
                    </div>
                  </div>

                  {/* Performance Table */}
                  <div className="bg-slate-50 rounded-xl border">
                    <div className="px-4 py-3 border-b bg-slate-100 flex items-center justify-between">
                      <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                        <Award className="w-5 h-5 text-amber-500" />
                        Rendimiento por Promoción
                      </h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="text-left px-4 py-3 font-medium text-slate-600">Promoción</th>
                            <th className="text-center px-4 py-3 font-medium text-slate-600">Vistas</th>
                            <th className="text-center px-4 py-3 font-medium text-slate-600">Clicks</th>
                            <th className="text-center px-4 py-3 font-medium text-slate-600">Ventas</th>
                            <th className="text-center px-4 py-3 font-medium text-slate-600">Conversión</th>
                            <th className="text-right px-4 py-3 font-medium text-slate-600">Ingresos</th>
                            <th className="text-center px-4 py-3 font-medium text-slate-600">Estado</th>
                            <th className="text-center px-4 py-3 font-medium text-slate-600">Detalle</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                          {salesData.promotions?.map((promo, idx) => (
                            <tr key={idx} className="hover:bg-white">
                              <td className="px-4 py-3">
                                <div>
                                  <p className="font-medium text-slate-800">{promo.title}</p>
                                  <p className="text-xs text-slate-400">
                                    ${promo.promo_price} <span className="line-through">${promo.original_price}</span>
                                    <span className="text-green-600 ml-1">-{promo.discount_percent}%</span>
                                  </p>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-center text-slate-600">{promo.views}</td>
                              <td className="px-4 py-3 text-center text-slate-600">{promo.clicks}</td>
                              <td className="px-4 py-3 text-center font-semibold text-slate-800">{promo.sales}</td>
                              <td className="px-4 py-3 text-center">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  promo.overall_conversion_rate >= 5 ? 'bg-green-100 text-green-700' :
                                  promo.overall_conversion_rate >= 2 ? 'bg-amber-100 text-amber-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {promo.overall_conversion_rate}%
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right font-semibold text-green-600">
                                ${promo.revenue?.toLocaleString() || 0}
                              </td>
                              <td className="px-4 py-3 text-center">
                                {promo.is_active ? (
                                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">Activa</span>
                                ) : (
                                  <span className="px-2 py-1 bg-slate-100 text-slate-500 rounded-full text-xs">Inactiva</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <button
                                  onClick={() => fetchPromoDetail(promo.promo_id, promo.title)}
                                  className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-lg text-xs hover:bg-indigo-200 transition-colors flex items-center gap-1 mx-auto"
                                >
                                  <Calendar className="w-3 h-3" />
                                  Ver días
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Strategic Insights */}
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-200">
                    <h3 className="font-semibold text-indigo-800 mb-3 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />
                      💡 Insights para tu Estrategia
                    </h3>
                    <div className="space-y-2 text-sm text-indigo-700">
                      {salesData.promotions?.length > 0 && (
                        <>
                          {salesData.promotions[0]?.revenue > 0 && (
                            <p>⭐ <strong>{salesData.promotions[0]?.title}</strong> es tu promoción más exitosa con ${salesData.promotions[0]?.revenue?.toLocaleString()} en ventas</p>
                          )}
                          {salesData.promotions?.some(p => p.views > 50 && p.sales === 0) && (
                            <p>⚠️ Hay promociones con muchas vistas pero sin ventas. Considera ajustar el precio o la oferta.</p>
                          )}
                          {salesData.promotions?.some(p => p.overall_conversion_rate >= 10) && (
                            <p>🎯 Tienes promociones con conversión alta (&gt;10%). ¡Replica esta estrategia!</p>
                          )}
                          {salesData.summary?.total_revenue === 0 && (
                            <p>📊 Aún no tienes ventas registradas. Las ventas se contarán cuando los clientes compren desde el menú digital.</p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No hay datos de ventas disponibles</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t bg-slate-50 flex justify-end">
              <button
                onClick={() => setShowSalesAnalytics(false)}
                className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Promo Detail Modal - Sales by Day/Hour */}
      {showPromoDetail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b bg-gradient-to-r from-indigo-500 to-purple-500 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Calendar className="w-6 h-6 text-white" />
                <div>
                  <h2 className="text-xl font-bold text-white">{promoDetailData?.title || 'Detalle de Promoción'}</h2>
                  <p className="text-white/70 text-sm">Historial de ventas por día y hora</p>
                </div>
              </div>
              <button 
                onClick={() => setShowPromoDetail(false)}
                className="text-white/80 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              {loadingPromoDetail ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
                </div>
              ) : promoDetailData ? (
                <div className="space-y-6">
                  {/* Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200 text-center">
                      <p className="text-2xl font-bold text-indigo-800">{promoDetailData.total_sales || 0}</p>
                      <p className="text-xs text-indigo-600">Ventas Totales</p>
                    </div>
                    <div className="bg-green-50 rounded-xl p-4 border border-green-200 text-center">
                      <p className="text-2xl font-bold text-green-800">${promoDetailData.total_revenue?.toLocaleString() || 0}</p>
                      <p className="text-xs text-green-600">Ingresos</p>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-200 text-center">
                      <p className="text-2xl font-bold text-blue-800">{promoDetailData.total_units || 0}</p>
                      <p className="text-xs text-blue-600">Unidades</p>
                    </div>
                    <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 text-center">
                      <p className="text-2xl font-bold text-amber-800">${promoDetailData.total_savings_given?.toLocaleString() || 0}</p>
                      <p className="text-xs text-amber-600">Descuentos</p>
                    </div>
                  </div>

                  {/* Daily Chart */}
                  {promoDetailData.daily_chart?.length > 0 ? (
                    <div className="bg-slate-50 rounded-xl p-4 border">
                      <h3 className="font-semibold text-slate-700 mb-4 flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-indigo-500" />
                        Ventas por Día
                      </h3>
                      <div className="space-y-2">
                        {promoDetailData.daily_chart.map((day, idx) => (
                          <div key={idx} className="flex items-center gap-4 p-3 bg-white rounded-lg border">
                            <div className="w-24 text-sm font-medium text-slate-600">
                              {new Date(day.date + 'T12:00:00').toLocaleDateString('es-MX', { 
                                weekday: 'short', 
                                day: 'numeric', 
                                month: 'short' 
                              })}
                            </div>
                            <div className="flex-1">
                              <div className="h-6 bg-slate-200 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all"
                                  style={{ 
                                    width: `${Math.min(100, (day.revenue / Math.max(...promoDetailData.daily_chart.map(d => d.revenue || 1))) * 100)}%` 
                                  }}
                                />
                              </div>
                            </div>
                            <div className="text-right min-w-[100px]">
                              <p className="font-semibold text-slate-800">${day.revenue?.toLocaleString()}</p>
                              <p className="text-xs text-slate-500">{day.sales} venta{day.sales !== 1 ? 's' : ''}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-slate-50 rounded-xl p-8 border text-center">
                      <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No hay ventas registradas por día</p>
                      <p className="text-xs text-slate-400 mt-1">Las ventas aparecerán aquí cuando los clientes compren</p>
                    </div>
                  )}

                  {/* Recent Sales */}
                  {promoDetailData.recent_sales?.length > 0 && (
                    <div className="bg-slate-50 rounded-xl p-4 border">
                      <h3 className="font-semibold text-slate-700 mb-4 flex items-center gap-2">
                        <Clock className="w-5 h-5 text-green-500" />
                        Últimas Ventas (con hora exacta)
                      </h3>
                      <div className="space-y-2 max-h-[300px] overflow-y-auto">
                        {promoDetailData.recent_sales.map((sale, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                <ShoppingCart className="w-5 h-5 text-green-600" />
                              </div>
                              <div>
                                <p className="font-medium text-slate-800">{sale.customer_name || 'Cliente'}</p>
                                <p className="text-xs text-slate-500">{sale.customer_phone || 'Sin teléfono'}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-green-600">${sale.revenue?.toLocaleString()}</p>
                              <p className="text-xs text-slate-400">
                                {new Date(sale.timestamp).toLocaleDateString('es-MX', {
                                  day: '2-digit',
                                  month: 'short',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* No Sales Message */}
                  {(!promoDetailData.recent_sales || promoDetailData.recent_sales.length === 0) && (
                    <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                      <p className="text-amber-800 text-sm flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Esta promoción aún no tiene ventas registradas. Las ventas se contarán cuando los clientes compren desde el menú digital.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No hay datos disponibles</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t bg-slate-50 flex justify-end">
              <button
                onClick={() => setShowPromoDetail(false)}
                className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Carousel Manager Modal */}
      {showCarouselManager && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="carousel-manager-modal">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b bg-gradient-to-r from-orange-500 to-amber-500 text-white flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Smartphone className="w-6 h-6" />
                  Carrusel Inteligente
                </h2>
                <p className="text-sm text-orange-100">Gestiona las Ofertas del Día en tu menú digital</p>
              </div>
              <button
                onClick={() => setShowCarouselManager(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingCarousel ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                </div>
              ) : (
                <>
                  {/* Current Carousel Preview */}
                  <div className="mb-6">
                    <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <Eye className="w-4 h-4" />
                      Vista Previa del Carrusel ({carouselItems.length} items)
                    </h3>
                    {carouselItems.length === 0 ? (
                      <div className="bg-amber-50 rounded-xl p-6 text-center border border-amber-200">
                        <Smartphone className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                        <p className="text-amber-800 font-medium">El carrusel está vacío</p>
                        <p className="text-amber-600 text-sm mt-1">Activa promociones o destaca productos para llenar el carrusel</p>
                      </div>
                    ) : (
                      <div className="flex gap-4 overflow-x-auto pb-4">
                        {carouselItems.map((item, idx) => (
                          <div 
                            key={`${item.type}-${item.id}-${idx}`}
                            className={`flex-shrink-0 w-48 rounded-xl overflow-hidden shadow-md border-2 ${
                              item.is_promo ? 'border-purple-400' : 'border-orange-400'
                            }`}
                          >
                            <div className="h-28 bg-slate-100 relative">
                              {item.image_url ? (
                                <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Image className="w-8 h-8 text-slate-300" />
                                </div>
                              )}
                              <span className={`absolute top-2 left-2 px-2 py-0.5 rounded text-xs font-bold text-white ${
                                item.is_promo ? 'bg-purple-500' : 'bg-orange-500'
                              }`}>
                                {item.is_promo ? 'PROMO' : 'DESTACADO'}
                              </span>
                            </div>
                            <div className="p-3 bg-white">
                              <p className="font-medium text-sm truncate">{item.title}</p>
                              <div className="flex items-center gap-2 mt-1">
                                {item.is_promo && item.original_price !== item.promo_price && (
                                  <span className="text-xs text-slate-400 line-through">${item.original_price}</span>
                                )}
                                <span className="text-sm font-bold text-orange-600">${item.promo_price || item.original_price}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Add Products to Carousel */}
                  <div className="border-t pt-6">
                    <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      Agregar Productos Destacados
                    </h3>
                    <p className="text-sm text-slate-500 mb-4">
                      Selecciona productos de tu catálogo para mostrar en el carrusel junto con las promociones activas.
                    </p>
                    
                    {/* Search */}
                    <div className="relative mb-4">
                      <input
                        type="text"
                        placeholder="Buscar producto..."
                        value={productSearch}
                        onChange={(e) => setProductSearch(e.target.value)}
                        className="w-full px-4 py-2 pl-10 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        data-testid="product-search-input"
                      />
                      <Target className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    </div>

                    {/* Products Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-64 overflow-y-auto">
                      {products
                        .filter(p => p.name?.toLowerCase().includes(productSearch.toLowerCase()))
                        .slice(0, 20)
                        .map((product) => {
                          const isFeatured = product.featured === true;
                          return (
                            <div 
                              key={product.product_id}
                              className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                isFeatured 
                                  ? 'border-orange-500 bg-orange-50' 
                                  : 'border-slate-200 hover:border-orange-300 bg-white'
                              }`}
                              onClick={() => toggleFeatured(product.product_id, isFeatured)}
                              data-testid={`product-${product.product_id}`}
                            >
                              <div className="h-16 bg-slate-100 rounded mb-2 overflow-hidden">
                                {product.image_url ? (
                                  <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Image className="w-6 h-6 text-slate-300" />
                                  </div>
                                )}
                              </div>
                              <p className="text-xs font-medium truncate">{product.name}</p>
                              <div className="flex items-center justify-between mt-1">
                                <span className="text-xs text-slate-500">${product.price}</span>
                                {isFeatured && (
                                  <span className="text-xs text-orange-600 font-bold">✓</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>

                  {/* Tips */}
                  <div className="mt-6 bg-blue-50 rounded-xl p-4 border border-blue-200">
                    <h4 className="font-medium text-blue-800 flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4" />
                      Consejos para un carrusel efectivo
                    </h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• Mantén 2-4 items para mejor impacto visual</li>
                      <li>• Combina promociones con productos destacados</li>
                      <li>• Las promociones activas se muestran automáticamente</li>
                      <li>• Los productos destacados complementan las ofertas</li>
                    </ul>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t bg-slate-50 flex justify-end gap-3">
              <button
                onClick={() => setShowCarouselManager(false)}
                className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Leads Panel Modal */}
      {showLeadsPanel && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="leads-panel-modal">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b bg-gradient-to-r from-cyan-500 to-blue-500 text-white flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Users className="w-6 h-6" />
                  Leads Interesados
                </h2>
                <p className="text-sm text-cyan-100">Clientes que quieren recibir ofertas</p>
              </div>
              <button
                onClick={() => setShowLeadsPanel(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Stats */}
            {leadsStats && (
              <div className="px-6 py-4 bg-slate-50 border-b grid grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-800">{leadsStats.total}</p>
                  <p className="text-xs text-slate-500">Total</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{leadsStats.new}</p>
                  <p className="text-xs text-slate-500">Nuevos</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-600">{leadsStats.contacted}</p>
                  <p className="text-xs text-slate-500">Contactados</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{leadsStats.converted}</p>
                  <p className="text-xs text-slate-500">Convertidos</p>
                </div>
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingLeads ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
                </div>
              ) : leads.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 font-medium">No hay leads todavía</p>
                  <p className="text-slate-400 text-sm mt-1">Cuando alguien deje su teléfono para recibir ofertas, aparecerá aquí</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {leads.map((lead) => (
                    <div key={lead.id} className="p-4 bg-slate-50 rounded-xl border flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                          {(lead.name || lead.phone || '?')[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{lead.name || 'Sin nombre'}</p>
                          <p className="text-sm text-slate-500">{lead.phone}</p>
                          {lead.promo_title && (
                            <p className="text-xs text-cyan-600 mt-1">Interesado en: {lead.promo_title}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* WhatsApp Button */}
                        <a
                          href={`https://wa.me/${lead.phone_normalized}?text=${encodeURIComponent('Hola! Vi que te interesaron nuestras promociones...')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                          title="Contactar por WhatsApp"
                        >
                          <MessageCircle className="w-4 h-4" />
                        </a>
                        {/* Status Select */}
                        <select
                          value={lead.status}
                          onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                          className={`px-3 py-2 rounded-lg text-sm font-medium border-0 ${
                            lead.status === 'new' ? 'bg-blue-100 text-blue-700' :
                            lead.status === 'contacted' ? 'bg-amber-100 text-amber-700' :
                            lead.status === 'converted' ? 'bg-green-100 text-green-700' :
                            'bg-slate-100 text-slate-700'
                          }`}
                        >
                          <option value="new">Nuevo</option>
                          <option value="contacted">Contactado</option>
                          <option value="converted">Convertido</option>
                          <option value="not_interested">No interesado</option>
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t bg-slate-50 flex justify-end">
              <button
                onClick={() => setShowLeadsPanel(false)}
                className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoStudio;
